/**
 * Failure reporting for paths that deliberately swallow errors.
 *
 * WHY THIS EXISTS: `users/{uid}/pageViews` had no Firestore rules block, so
 * every write was default-denied — and pageViewService.log() caught the error
 * and returned silently. The collection stayed empty app-wide for months: the
 * home 🔥 streak never lit up for any member and the admin dashboard's
 * passive-activity signal read nothing, with no console output and no visible
 * symptom. A silent catch that hides a PERMANENTLY broken write path is
 * indistinguishable from success.
 *
 * The swallowing itself is usually right — telemetry must never block
 * navigation, and a failed resume-position write shouldn't break the lesson
 * page. What was wrong is that it was silent. So: keep swallowing, but count
 * and surface.
 *
 * Usage:
 *   import { noteFailure, failureReport } from '@/utils/silentFailure';
 *   try { … } catch (e) { noteFailure('pageViewService.log', e); return false; }
 *
 * In dev: warns on the console, once per scope per session (repeat failures
 * increment silently so a broken path doesn't spam a loop).
 * Anywhere: `window.__ypFailures()` prints the tally.
 */

const counts = new Map();
const warned = new Set();

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Record a swallowed failure.
 * @param {string} scope dotted identifier, e.g. 'pageViewService.log'
 * @param {unknown} err the caught error
 */
export function noteFailure(scope, err) {
  const key = String(scope || 'unknown');
  const entry = counts.get(key) || { count: 0, lastMessage: '', firstAt: Date.now() };
  entry.count += 1;
  entry.lastMessage = (err && (err.code || err.message)) || String(err || '');
  entry.lastAt = Date.now();
  counts.set(key, entry);

  if (isDev && !warned.has(key)) {
    warned.add(key);
    // Once per scope — a denied write inside a loop shouldn't flood the console.
    console.warn(
      `[silent-failure] ${key} failed and was swallowed: ${entry.lastMessage}\n` +
      `  This path hides its errors by design. If it never succeeds, the feature ` +
      `is silently dead. Run window.__ypFailures() for the tally.`
    );
  }
}

/** Snapshot of every swallowed failure this session. */
export function failureReport() {
  return [...counts.entries()]
    .map(([scope, v]) => ({ scope, ...v }))
    .sort((a, b) => b.count - a.count);
}

// Always attached (not dev-only): if a member reports "the streak never shows",
// this is answerable from their own console without a special build.
if (typeof window !== 'undefined') {
  window.__ypFailures = () => {
    const rows = failureReport();
    if (!rows.length) {
      console.log('[silent-failure] no swallowed failures recorded this session.');
      return rows;
    }
    console.table(rows.map((r) => ({ scope: r.scope, count: r.count, lastError: r.lastMessage })));
    return rows;
  };
}
